import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, Calendar, Clock, MapPin, Phone, User, CheckCircle, XCircle, Check, 
  CalendarDays, Users, TrendingUp, AlertTriangle, Briefcase, CreditCard, Smartphone, ArrowRight, FolderOpen
} from 'lucide-react';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import TodayRouteCard from '../components/TodayRouteCard';
import PaymentSummaryCard from '../components/PaymentSummaryCard';
import TrialReminderBanner from '../components/TrialReminderBanner';
import AccountInactiveNotice from '../components/AccountInactiveNotice';
import AddAppointmentDialog from '../components/AddAppointmentDialog';
import AddClientDialog from '../components/AddClientDialog';
import QuickBlockDialog from '../components/QuickBlockDialog';
import GettingStartedChecklist from '../components/help/GettingStartedChecklist';
import WeeklyReviewReminder from '../components/WeeklyReviewReminder';
import BookingReminderBanner from '../components/BookingReminderBanner';
import ProductTour from '../components/help/ProductTour';
import { PlayCircle } from 'lucide-react';
import { shouldAutoOpenTour } from '../lib/tutorial';
import RemindersList from '../components/RemindersList';
import VoiceRecorder from '../components/VoiceRecorder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardHomePage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [todayBookings, setTodayBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [emergencyRequests, setEmergencyRequests] = useState([]);
  const [providerProfile, setProviderProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [tourOpen, setTourOpen] = useState(false);
  const [reminderReload, setReminderReload] = useState(0);

  useEffect(() => {
    loadProviderProfile();
    loadBookings();
    loadMetrics();
    loadEmergencyRequests();
    // First-time providers get the tour once (skippable). Returning users don't.
    if (shouldAutoOpenTour(user?.id)) {
      const t = setTimeout(() => setTourOpen(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const loadProviderProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviderProfile(response.data);
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadMetrics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(response.data);
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  };

  const loadEmergencyRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/emergency-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmergencyRequests(response.data);
    } catch (error) {
      console.error('Error loading emergency requests:', error);
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await axios.get(`${API_URL}/api/dashboard/bookings`, {
        params: {
          start_date: today,
          end_date: nextWeek
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const bookings = response.data;
      
      // Split into today and upcoming
      const today_list = bookings.filter(b => {
        const bookingDate = parseISO(b.startDateTime);
        return isToday(bookingDate);
      });
      
      const upcoming_list = bookings.filter(b => {
        const bookingDate = parseISO(b.startDateTime);
        return !isToday(bookingDate);
      });
      
      setTodayBookings(today_list);
      setUpcomingBookings(upcoming_list);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      setActionLoading({ ...actionLoading, [bookingId]: status });
      
      await axios.patch(
        `${API_URL}/api/dashboard/bookings/${bookingId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Booking ${status.toLowerCase()}`);
      loadBookings();
      loadMetrics();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking');
    } finally {
      setActionLoading({ ...actionLoading, [bookingId]: null });
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      CONFIRMED: 'bg-green-100 text-green-800 border-green-300',
      COMPLETED: 'bg-blue-100 text-blue-800 border-blue-300',
      CANCELED: 'bg-red-100 text-red-800 border-red-300',
    };
    
    return (
      <Badge className={`${variants[status] || ''} border`}>
        {status}
      </Badge>
    );
  };

  const BookingCard = ({ booking }) => {
    const startDate = parseISO(booking.startDateTime);
    const isLoadingAction = actionLoading[booking.id];
    
    return (
      <Card className="hover:shadow-md transition-shadow" data-testid={`booking-card-${booking.id}`}>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg">{booking.service?.name || 'Service'}</CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <Calendar className="h-3 w-3" />
                {format(startDate, 'MMM d, yyyy')}
                <Clock className="h-3 w-3 ml-2" />
                {format(startDate, 'h:mm a')}
              </CardDescription>
            </div>
            {getStatusBadge(booking.status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <User className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">{booking.client?.name}</p>
                <p className="text-muted-foreground">{booking.client?.phone}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-muted-foreground">{booking.locationAddress}</p>
                {booking.locationNotes && (
                  <p className="text-xs text-muted-foreground mt-1">{booking.locationNotes}</p>
                )}
              </div>
            </div>
            
            {booking.horseCount > 1 && (
              <p className="text-muted-foreground">Horses: {booking.horseCount}</p>
            )}
            
            {/* Riding lesson info */}
            {booking.studentName && (
              <div className="mt-3 pt-3 border-t">
                <p className="font-medium text-sm mb-1">Lesson Information:</p>
                <p className="text-sm text-muted-foreground">Student: {booking.studentName}</p>
                {booking.studentExperienceLevel && (
                  <p className="text-sm text-muted-foreground">
                    Level: {booking.studentExperienceLevel.charAt(0) + booking.studentExperienceLevel.slice(1).toLowerCase()}
                  </p>
                )}
                {booking.lessonType && (
                  <p className="text-sm text-muted-foreground">Notes: {booking.lessonType}</p>
                )}
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          {booking.status === 'PENDING' && (
            <div className="flex gap-2 pt-3 border-t">
              <Button
                size="sm"
                className="flex-1 h-10"
                onClick={() => updateBookingStatus(booking.id, 'CONFIRMED')}
                disabled={isLoadingAction}
                data-testid={`confirm-button-${booking.id}`}
              >
                {isLoadingAction === 'CONFIRMED' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Confirm
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-10"
                onClick={() => updateBookingStatus(booking.id, 'CANCELED')}
                disabled={isLoadingAction}
                data-testid={`cancel-button-${booking.id}`}
              >
                {isLoadingAction === 'CANCELED' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <XCircle className="mr-2 h-4 w-4" />
                    Cancel
                  </>
                )}
              </Button>
            </div>
          )}
          
          {booking.status === 'CONFIRMED' && (
            <div className="flex gap-2 pt-3 border-t">
              <Button
                size="sm"
                className="flex-1 h-10"
                onClick={() => updateBookingStatus(booking.id, 'COMPLETED')}
                disabled={isLoadingAction}
                data-testid={`complete-button-${booking.id}`}
              >
                {isLoadingAction === 'COMPLETED' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Mark Completed
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-10"
                onClick={() => updateBookingStatus(booking.id, 'CANCELED')}
                disabled={isLoadingAction}
              >
                {isLoadingAction === 'CANCELED' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <XCircle className="mr-2 h-4 w-4" />
                    Cancel
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const acceptEmergencyRequest = async (requestId) => {
    try {
      setActionLoading({ ...actionLoading, [`emergency-${requestId}`]: true });
      
      await axios.post(
        `${API_URL}/api/dashboard/emergency-requests/${requestId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Emergency request accepted! Contact the client ASAP.');
      loadEmergencyRequests();
    } catch (error) {
      console.error('Error accepting emergency request:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept request');
    } finally {
      setActionLoading({ ...actionLoading, [`emergency-${requestId}`]: false });
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      FARRIER: 'Farrier',
      VET: 'Veterinarian',
      HORSE_HAULING: 'Horse Hauling',
      FENCE_REPAIR: 'Fence Repair',
      RANCH_REPAIR: 'Ranch Repair',
      EQUIPMENT_REPAIR: 'Equipment Repair'
    };
    return labels[category] || category;
  };

  const MetricCard = ({ title, value, icon: Icon, color = 'primary' }) => (
    <Card className="relative overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1" data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}>
              {value}
            </p>
          </div>
          <div className={`p-3 rounded-full bg-${color}/10`}>
            <Icon className={`h-5 w-5 text-${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <ProductTour open={tourOpen} onOpenChange={setTourOpen} />

        {/* Trial / inactive notices (Session 26 — soft only, no payment-blocking) */}
        {providerProfile && (
          <div className="space-y-3" data-testid="provider-banners">
            <TrialReminderBanner provider={providerProfile} />
            <AccountInactiveNotice
              provider={providerProfile}
              accountStatus={providerProfile.accountStatus}
            />
          </div>
        )}

        {/* Getting Started checklist — detects setup from existing data; dismissible */}
        <GettingStartedChecklist onStartTour={() => setTourOpen(true)} />

        {/* Weekly Review reminder — completed jobs awaiting approval */}
        <WeeklyReviewReminder />

        {/* Booking reminders (RW-BUILD-013) — appointments within 48h */}
        <BookingReminderBanner
          bookings={[...todayBookings, ...upcomingBookings]}
          role="provider"
          onView={() => navigate('/dashboard/bookings')}
        />

        {/* Daily reminders (RW-BUILD-005) — due now, with quick actions */}
        <RemindersList scope="due" reloadSignal={reminderReload} hideWhenEmpty />

        {/* Quick actions row (Session 27/28) — Add Client, Add Appointment, Block Day */}
        <div
          className="rounded-lg border bg-card p-4 flex flex-wrap items-center gap-2"
          data-testid="quick-actions"
        >
          <span className="text-sm font-medium text-muted-foreground mr-2 hidden sm:inline">
            Quick actions:
          </span>
          <AddClientDialog onCreated={() => { loadMetrics(); }} />
          <AddAppointmentDialog onCreated={() => { loadMetrics(); loadBookings(); }} />
          <QuickBlockDialog onBlocked={() => { loadMetrics(); loadBookings(); }} />
          <VoiceRecorder variant="button" onSaved={() => setReminderReload((r) => r + 1)} />
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard/notes')}
            data-testid="saved-voice-notes-btn"
          >
            <FolderOpen className="h-4 w-4 mr-1" /> Saved Voice Notes
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-muted-foreground"
            onClick={() => setTourOpen(true)}
            data-testid="start-tour-btn"
          >
            <PlayCircle className="h-4 w-4 mr-1" /> Tour
          </Button>
        </div>

        {/* Metrics Section */}
        {metrics && (
          <div>
            <h2 className="text-2xl font-bold mb-4" data-testid="metrics-heading">
              Overview
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard/bookings?status=CONFIRMED')}
                className="text-left"
                data-testid="metric-upcoming-card"
              >
                <Card className="relative overflow-hidden cursor-pointer hover:shadow-md hover:border-primary/40 transition">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Upcoming</p>
                        <p className="text-2xl font-bold mt-1" data-testid="metric-upcoming-bookings">
                          {metrics.upcomingBookings}
                        </p>
                      </div>
                      <div className="p-3 rounded-full bg-blue-100">
                        <CalendarDays className="h-5 w-5 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>

              <button
                type="button"
                onClick={() => navigate('/dashboard/bookings?status=COMPLETED')}
                className="text-left"
                data-testid="metric-completed-card"
              >
                <Card className="relative overflow-hidden cursor-pointer hover:shadow-md hover:border-primary/40 transition">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Completed</p>
                        <p className="text-2xl font-bold mt-1" data-testid="metric-completed-jobs">
                          {metrics.completedJobs}
                        </p>
                      </div>
                      <div className="p-3 rounded-full bg-green-100">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>

              <button
                type="button"
                onClick={() => navigate('/dashboard/clients')}
                className="text-left"
                data-testid="metric-clients-card"
              >
                <Card className="relative overflow-hidden cursor-pointer hover:shadow-md hover:border-primary/40 transition">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Clients</p>
                        <p className="text-2xl font-bold mt-1" data-testid="metric-total-clients">
                          {metrics.totalClients}
                        </p>
                      </div>
                      <div className="p-3 rounded-full bg-purple-100">
                        <Users className="h-5 w-5 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>

              <button
                type="button"
                onClick={() => navigate('/dashboard/clients')}
                className="text-left"
                data-testid="metric-animals-card"
              >
                <Card className="relative overflow-hidden cursor-pointer hover:shadow-md hover:border-primary/40 transition">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Animals</p>
                        <p className="text-2xl font-bold mt-1" data-testid="metric-total-horses">
                          {metrics.totalHorses}
                        </p>
                      </div>
                      <div className="p-3 rounded-full bg-amber-100">
                        <Briefcase className="h-5 w-5 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>

              <button
                type="button"
                onClick={() => navigate('/dashboard/calendar')}
                className="text-left"
                data-testid="metric-this-week-card"
              >
                <Card className="relative overflow-hidden cursor-pointer hover:shadow-md hover:border-primary/40 transition">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">This Week</p>
                        <p className="text-2xl font-bold mt-1" data-testid="metric-bookings-this-week">
                          {metrics.bookingsThisWeek}
                        </p>
                      </div>
                      <div className="p-3 rounded-full bg-indigo-100">
                        <Calendar className="h-5 w-5 text-indigo-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>

              <button
                type="button"
                onClick={() => navigate('/dashboard/calendar')}
                className="text-left"
                data-testid="metric-this-month-card"
              >
                <Card className="relative overflow-hidden cursor-pointer hover:shadow-md hover:border-primary/40 transition">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">This Month</p>
                        <p className="text-2xl font-bold mt-1" data-testid="metric-bookings-this-month">
                          {metrics.bookingsThisMonth}
                        </p>
                      </div>
                      <div className="p-3 rounded-full bg-teal-100">
                        <TrendingUp className="h-5 w-5 text-teal-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
            </div>
          </div>
        )}

        {/* Emergency Requests Section */}
        {emergencyRequests.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2" data-testid="emergency-heading">
              <AlertTriangle className="h-6 w-6 text-red-500" />
              Emergency Requests
            </h2>
            <div className="grid gap-4" data-testid="emergency-requests">
              {emergencyRequests.map((request) => (
                <Card key={request.id} className="border-l-4 border-l-red-500" data-testid={`emergency-card-${request.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          {getCategoryLabel(request.category)}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Clock className="h-3 w-3" />
                          {format(new Date(request.createdAt), 'MMM d, yyyy h:mm a')}
                        </CardDescription>
                      </div>
                      <Badge className="bg-red-100 text-red-800 border-red-300">
                        URGENT
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="font-medium">{request.clientName}</p>
                          <p className="text-muted-foreground">{request.clientPhone}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <p className="text-muted-foreground">{request.location}</p>
                      </div>
                      <p className="text-muted-foreground bg-muted p-2 rounded">
                        {request.description}
                      </p>
                    </div>
                    <Button
                      className="w-full bg-red-600 hover:bg-red-700"
                      onClick={() => acceptEmergencyRequest(request.id)}
                      disabled={actionLoading[`emergency-${request.id}`]}
                      data-testid={`accept-emergency-${request.id}`}
                    >
                      {actionLoading[`emergency-${request.id}`] ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Accept Job
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Today's Route — grouped by barn/location with stop ordering, animals, payment status */}
        <div>
          <TodayRouteCard />
        </div>

        {/* Field View shortcut — one-tap path to mobile field workflow */}
        <Card
          className="border-primary/30 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors"
          onClick={() => navigate('/dashboard/field')}
          data-testid="field-view-cta"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="bg-primary text-primary-foreground rounded-full p-3 flex-shrink-0">
              <Smartphone className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold">Open Field View</p>
              <p className="text-sm text-muted-foreground">
                Today's jobs, ready for your truck — mark done, paid, or add notes in one tap.
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-primary flex-shrink-0" />
          </CardContent>
        </Card>

        {/* End-of-Day Closeout shortcut */}
        <Card
          className="border-amber-300/50 bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => navigate('/dashboard/closeout')}
          data-testid="closeout-cta"
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="bg-amber-500 text-white rounded-full p-3 flex-shrink-0">
              <Briefcase className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold">End-of-Day Closeout</p>
              <p className="text-sm text-muted-foreground">
                Review today, clean up unfinished jobs, and see what still needs payment or an invoice.
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-amber-600 flex-shrink-0" />
          </CardContent>
        </Card>

        {/* Payment Tracker — manual payment tracking summary */}
        <div>
          <PaymentSummaryCard />
        </div>

        {/* Upcoming Bookings */}
        <div>
          <h2 className="text-2xl font-bold mb-4" data-testid="upcoming-heading">
            Upcoming (Next 7 Days)
          </h2>
          {upcomingBookings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No upcoming bookings</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4" data-testid="upcoming-bookings">
              {upcomingBookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} />
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
